import { FOLGA_MAXIMA_TRECHO_M, folgaDoTrecho } from './folgaCorda'
import { localizarPontoNoMundo } from './ancoragem'
import type { FuncaoCorda } from './funcoesCorda'
import type { CenaTabletop, CordaTabletop, VetorTabletop } from './tipos'

/**
 * Catenária das cordas: quanto cada trecho pende, pelo peso da própria corda e
 * pela tensão que ela leva, e como a folga aparece quando as pontas se mexem.
 *
 * Modelo (parábola, vão pequeno diante da flecha): com tensão horizontal `H` e
 * peso linear `w`, a flecha é `s = w·L²/(8H)` e a corda tem `ΔL = 8s²/(3L) =
 * w²L³/(24H²)` a mais que o vão reto. O desenho (`folgaCorda.ts`) recebe esse
 * `ΔL` como folga. Corda esticada por uma carga de dezenas de kgf quase não
 * pende; solta, ou com poucos kgf, pende bastante, e é isso que se vê.
 *
 * O peso linear é multiplicado por `EXAGERO_DIDATICO`: com o peso real (cerca de
 * 7 dag/m numa corda de 11 mm) uma corda de 5 m com 2 kgf pendia só uns 8 cm,
 * pouco para ver na tela. TypeScript puro.
 */

type V = VetorTabletop

/** Peso linear de uma corda estática de 11 mm: ~0,07 kg/m. */
export const PESO_LINEAR_CORDA_KGF_M = 0.07
/** Quanto o peso da corda é exagerado para a flecha ser visível (didático). */
export const EXAGERO_DIDATICO = 6
/** Tensão mínima considerada, em kgf: corda "sem" tensão não pende ao infinito. */
export const TENSAO_MINIMA_KGF = 0.3
const G = 9.80665

/** Quanto de corda há a mais que o vão reto `vaoM`, pendendo sob a tensão `tensaoKgf` (m). */
export function folgaPorTensao(vaoM: number, tensaoKgf: number, exagero = EXAGERO_DIDATICO): number {
  if (!(vaoM > 0)) return 0
  const H = Math.max(TENSAO_MINIMA_KGF, tensaoKgf) * G
  const w = PESO_LINEAR_CORDA_KGF_M * G * exagero
  const folga = (w * w * vaoM ** 3) / (24 * H * H)
  return Math.min(FOLGA_MAXIMA_TRECHO_M, folga < 0.005 ? 0 : folga)
}

/** Tensão típica, em kgf, de uma corda que não leva carga calculada: a que só segura o próprio peso. */
const TENSAO_DA_CORDA_FROUXA_KGF: Partial<Record<FuncaoCorda, number>> = {
  seguranca: 1.5,
}

/** A tensão com que o desenho considera a corda sem carga, ou `null` se ela é tratada como esticada. */
export const tensaoDaCordaFrouxaKgf = (funcao: FuncaoCorda): number | null => TENSAO_DA_CORDA_FROUXA_KGF[funcao] ?? null

const dist = (a: V, b: V) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2])

/** Os vãos retos de cada trecho do percurso, na cena; `null` onde falta um dos pontos. */
export function vaosDaCorda(cena: CenaTabletop, corda: CordaTabletop): (number | null)[] {
  const pontos = corda.percurso.map((ref) => localizarPontoNoMundo(cena, ref))
  return pontos.slice(1).map((p, i) => (p && pontos[i] ? dist(pontos[i]!, p) : null))
}

/**
 * A corda tem comprimento fixo: se as pontas se aproximam (a maca sobe e a
 * corda de segurança fica para trás), o que sobra do vão vira folga que pende;
 * se se afastam, a folga é consumida, até a corda ficar esticada. Compara a
 * cena de origem (onde a corda tinha a folga `folgaDoTrecho`) com a cena atual.
 */
export function folgasPorConservacao(cenaOrigem: CenaTabletop, cenaAtual: CenaTabletop, corda: CordaTabletop): number[] {
  const antes = vaosDaCorda(cenaOrigem, corda)
  const depois = vaosDaCorda(cenaAtual, corda)
  return antes.map((vao0, i) => {
    const vao1 = depois[i]
    const f0 = folgaDoTrecho(corda, i)
    if (vao0 === null || vao1 === null || vao1 === undefined) return f0
    const sobra = vao0 + f0 - vao1
    return Math.min(FOLGA_MAXIMA_TRECHO_M, Math.max(0, sobra < 0.005 ? 0 : sobra))
  })
}

/** Folga mínima de um vão não vertical, como fração dele: a "leve catenária" da corda esticada (flecha de ~3% do vão). */
export const FOLGA_LEVE_FRACAO_DO_VAO = 0.002
/** Acima desta fração vertical (|Δy|/vão) o trecho é tratado como pendurado reto: não ganha a folga leve. */
const FRACAO_VERTICAL_RETA = 0.9

/**
 * As folgas com que a corda é DESENHADA: a maior entre a que o autor definiu, a
 * que o peso próprio dá a uma corda sem carga e a folga leve que toda corda
 * tem, mesmo esticada (nenhuma é uma reta perfeita). Só o desenho usa isto; o
 * comprimento, as setas e as forças continuam com as folgas definidas. Fitas
 * ficam de fora: são chatas e abraçam a estrutura.
 */
export function folgasDeDesenho(cena: CenaTabletop, corda: CordaTabletop): readonly number[] | undefined {
  if (corda.funcao === 'fita') return corda.folgas
  const pontos = corda.percurso.map((ref) => localizarPontoNoMundo(cena, ref))
  const tensao = tensaoDaCordaFrouxaKgf(corda.funcao)
  const folgas = corda.percurso.slice(1).map((_, i) => {
    const base = folgaDoTrecho(corda, i)
    const a = pontos[i]
    const b = pontos[i + 1]
    if (!a || !b) return base
    const vao = dist(a, b)
    if (vao < 0.2) return base
    const vertical = Math.abs(b[1] - a[1]) / vao > FRACAO_VERTICAL_RETA
    const peso = tensao === null ? 0 : folgaPorTensao(vao, tensao)
    const leve = vertical ? 0 : Math.min(FOLGA_MAXIMA_TRECHO_M, vao * FOLGA_LEVE_FRACAO_DO_VAO)
    return Math.max(base, peso, leve)
  })
  return folgas.some((f) => f > 0) ? folgas : corda.folgas
}
